import React, { useEffect, useRef } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { Loader2, MapPin } from 'lucide-react';

import type { NormalizedAddress } from '../../../shared/AddressComponents/utils';
import { Card, CardContent } from '@/components/atoms/Card/Card';
import { useAddressGeocoding } from '../../../shared/AddressComponents/hooks/useAddressGeocoding';
import { extractPrimaryAddress } from '../../../shared/AddressComponents/utils';

const MapWithPin: React.FC<{
  position: google.maps.LatLngLiteral;
  companyName: string;
  address: string;
}> = ({ position, companyName, address }) => {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    if (typeof window === 'undefined') return;

    const googleApi = (window as typeof window & { google?: typeof google }).google;

    if (!googleApi?.maps) return;

    const map = new googleApi.maps.Map(mapRef.current, {
      center: position,
      zoom: 16,
      disableDefaultUI: true,
      clickableIcons: false,
    });

    const mapDiv = map.getDiv();
    const hideNonPrintableElements = () => {
      mapDiv.querySelectorAll('.gmnoprint, .gm-style-cc').forEach(element => {
        (element as HTMLElement).style.display = 'none';
      });
    };

    hideNonPrintableElements();
    const observer = new MutationObserver(hideNonPrintableElements);
    observer.observe(mapDiv, { childList: true, subtree: true });

    const marker = new googleApi.maps.Marker({
      position,
      map,
      title: companyName,
    });

    class TextOverlay extends googleApi.maps.OverlayView {
      private readonly position: google.maps.LatLngLiteral;
      private readonly text: string;
      private div?: HTMLElement;

      constructor(position: google.maps.LatLngLiteral, text: string) {
        super();
        this.position = position;
        this.text = text;
      }

      onAdd() {
        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.fontSize = '18px';
        this.div.style.fontWeight = '500';
        this.div.style.color = '#D73027';
        this.div.style.fontFamily = 'Arial, sans-serif';
        this.div.style.textShadow =
          '1px 1px 0 white, -1px -1px 0 white, 1px -1px 0 white, -1px 1px 0 white, 2px 2px 4px rgba(0,0,0,0.3)';
        this.div.style.whiteSpace = 'nowrap';
        this.div.style.pointerEvents = 'none';
        this.div.style.display = 'inline-flex';
        this.div.style.alignItems = 'center';
        this.div.style.gap = '6px';
        this.div.textContent = this.text;

        const panes = this.getPanes();

        if (panes) {
          panes.overlayLayer.appendChild(this.div);
        }
      }

      draw() {
        if (!this.div) return;

        const projection = this.getProjection();
        const mapInstance = this.getMap();

        if (!mapInstance) return;

        const point = projection.fromLatLngToDivPixel(
          new googleApi.maps.LatLng(this.position.lat, this.position.lng),
        );

        if (!point) return;

        const mapDiv = (mapInstance as google.maps.Map).getDiv() as HTMLElement | null;
        const containerWidth = mapDiv?.offsetWidth ?? 0;
        const textWidth = this.div.offsetWidth;
        const labelSpacing = 20;
        const shouldFlip = containerWidth
          ? point.x + labelSpacing + textWidth > containerWidth - 16
          : false;

        const verticalOffset = 16;

        if (shouldFlip) {
          this.div.style.transform = 'translate(-100%, -50%)';
          this.div.style.textAlign = 'right';
          this.div.style.left = `${point.x - labelSpacing}px`;
        } else {
          this.div.style.transform = 'translate(0, -50%)';
          this.div.style.textAlign = 'left';
          this.div.style.left = `${point.x + labelSpacing}px`;
        }

        this.div.style.top = `${point.y - verticalOffset}px`;
      }

      onRemove() {
        if (this.div?.parentNode) {
          this.div.parentNode.removeChild(this.div);
        }
      }
    }

    const textOverlay = new TextOverlay(position, companyName);
    textOverlay.setMap(map);

    return () => {
      observer.disconnect();
      marker.setMap(null);
      textOverlay.setMap(null);
    };
  }, [position, companyName, address]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%' }} />;
};

type GoogleMapsPinProps = {
  address?: string | null;
  companyName?: string | null;
  countryCode?: string | null;
  googleMapsApiKey: string;
  onAddressEnriched?: (address: NormalizedAddress) => void;
  className?: string;
};

const MapContent: React.FC<{
  address?: string | null;
  companyName?: string | null;
  countryCode?: string | null;
  onAddressEnriched?: (address: NormalizedAddress) => void;
}> = ({ address, companyName, countryCode, onAddressEnriched }) => {
  const {
    position,
    isLoading: isGeocoding,
    shouldShow,
  } = useAddressGeocoding(address, countryCode, onAddressEnriched);

  if (!address || !shouldShow) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-100">
        <div className="text-center">
          <MapPin className="mx-auto mb-2 h-12 w-12 text-blue-600" />
          <p className="text-sm font-medium text-gray-700">{companyName || 'Company Location'}</p>
          <p className="text-xs text-gray-500">Address not available</p>
        </div>
      </div>
    );
  }

  if (isGeocoding || !position) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-100">
        <div className="text-center">
          <Loader2 className="mx-auto mb-2 h-8 w-8 animate-spin text-gray-500" />
          <p className="text-sm text-gray-600">Loading location...</p>
        </div>
      </div>
    );
  }

  return (
    <MapWithPin
      position={position}
      companyName={companyName || 'Company Location'}
      address={extractPrimaryAddress(address)}
    />
  );
};

export const GoogleMapsPin: React.FC<GoogleMapsPinProps> = ({
  address,
  companyName,
  countryCode,
  googleMapsApiKey,
  onAddressEnriched,
  className = '',
}) => {
  if (!googleMapsApiKey) {
    return (
      <Card className={`flex size-full items-center justify-center ${className}`}>
        <CardContent className="p-6 text-center">
          <MapPin className="mx-auto mb-2 h-12 w-12 text-blue-600" />
          <p className="text-sm font-medium text-gray-700">{companyName || 'Company Location'}</p>
          <p className="mt-1 text-xs text-red-600">Google Maps API key required</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`h-full w-full ${className}`}>
      <APIProvider apiKey={googleMapsApiKey} libraries={['geocoding', 'places']}>
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          <MapContent
            address={address}
            companyName={companyName}
            countryCode={countryCode}
            onAddressEnriched={onAddressEnriched}
          />
        </div>
      </APIProvider>
    </div>
  );
};
